export type ChangeRow =
  | { op: 'add'; label: string; detail?: string }
  | { op: 'remove'; label: string; detail?: string }
  | { op: 'change'; label: string; from: unknown; to: unknown }

// Fields that are internal IDs or technical identifiers — never shown to end users.
const INTERNAL_FIELDS = new Set(['id', 'canonicalId', 'slug'])

// Returns true when a value is a non-empty array of objects (renders as "[object Object],...").
function isArrayOfObjects(v: unknown): boolean {
  return Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && v[0] !== null
}

// Returns true when a value is a plain (non-array) object.
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

// Returns a copy of beforeState with internal fields removed, for display in disclosures.
export function filterDisplayState(state: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(state).filter(([k]) => !INTERNAL_FIELDS.has(k)))
}

const TOOL_NAME_MAP: Record<string, string> = {
  update_account_roles: 'update account roles',
  set_account_disabled: 'set account disabled',
  update_collection_products: 'update collection products',
  update_retailer_collection: 'update retailer collection',
  create_retailer_collection: 'create retailer collection',
  // Grow this as new tools land. Unknown tools fall back to space-separated.
}

export function humanizeToolName(toolName: string): string {
  return TOOL_NAME_MAP[toolName] ?? toolName.replaceAll('_', ' ')
}

export function hasContext(beforeState: Record<string, unknown>): boolean {
  return Object.values(beforeState).some(
    v => v != null && v !== '' && (!Array.isArray(v) || v.length > 0)
  )
}

export function diffChanges(
  beforeState: Record<string, unknown>,
  proposedChanges: Record<string, unknown>
): ChangeRow[] {
  const rows: ChangeRow[] = []

  for (const [key, value] of Object.entries(proposedChanges)) {
    if (key.startsWith('add') && key.endsWith('Ids') && Array.isArray(value)) {
      for (const id of value) rows.push({ op: 'add', label: prettifyId(key, id, beforeState) })
      continue
    }
    if (key.startsWith('remove') && key.endsWith('Ids') && Array.isArray(value)) {
      for (const id of value) rows.push({ op: 'remove', label: prettifyId(key, id, beforeState) })
      continue
    }
    // Skip internal/technical fields (IDs, canonical identifiers).
    if (INTERNAL_FIELDS.has(key)) continue

    const from = key in beforeState ? beforeState[key] : undefined

    // Skip no-op rows where the value is identical before and after.
    if (JSON.stringify(from) === JSON.stringify(value)) continue

    // Skip diffs where either side is an array of objects — they can't be
    // meaningfully rendered inline (would display as "[object Object],...").
    if (isArrayOfObjects(value) || isArrayOfObjects(from)) continue

    // When the proposed value is a plain nested object (e.g. a GraphQL "input"
    // type wrapper like create_retailer_collection's `input` arg), flatten it
    // one level so individual field changes are shown instead of "[object Object]".
    if (isPlainObject(value)) {
      const fromObj = isPlainObject(from) ? from : {}
      for (const [subKey, subValue] of Object.entries(value)) {
        if (INTERNAL_FIELDS.has(subKey)) continue
        const subFrom = subKey in fromObj ? fromObj[subKey] : undefined
        if (JSON.stringify(subFrom) === JSON.stringify(subValue)) continue
        if (isPlainObject(subValue)) continue
        // Arrays of objects can't be shown inline, but delta keys (productsToAdd,
        // productsToDelete) get a count summary so the card isn't empty.
        if (isArrayOfObjects(subValue) || isArrayOfObjects(subFrom)) {
          if (Array.isArray(subValue) && subValue.length > 0) {
            if (/ToAdd$/i.test(subKey)) {
              const noun = subKey.replace(/ToAdd$/i, '').replace(/^[A-Z]/, c => c.toLowerCase())
              rows.push({ op: 'add', label: `${subValue.length} ${noun}` })
            } else if (/To(?:Delete|Remove)$/i.test(subKey)) {
              const noun = subKey
                .replace(/To(?:Delete|Remove)$/i, '')
                .replace(/^[A-Z]/, c => c.toLowerCase())
              rows.push({ op: 'remove', label: `${subValue.length} ${noun}` })
            }
          }
          continue
        }
        rows.push({ op: 'change', label: subKey, from: subFrom, to: subValue })
      }
      continue
    }

    rows.push({ op: 'change', label: key, from, to: value })
  }

  return rows
}

function prettifyId(key: string, id: string, beforeState: Record<string, unknown>): string {
  // 'addRoleIds' → singular 'role', collection 'roles'
  const stripped = key.replace(/^(add|remove)/, '').replace(/Ids$/, '')
  const singular = stripped.charAt(0).toLowerCase() + stripped.slice(1)
  const collection = singular + 's'
  const items = beforeState[collection]
  if (Array.isArray(items)) {
    const match = items.find((it: { id?: unknown; name?: unknown }) => String(it.id) === String(id))
    if (match && typeof match.name === 'string') return match.name
  }
  return `${singular} ${id}`
}
